import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { LandingNavbar } from "@/components/landing-navbar";
import { Footer } from "@/components/footer";
import { SEO, schemas } from "@/components/SEO";
import { ArticleCard } from "./components/ArticleCard";
import { ArrowLeft, Rocket, ChevronRight } from "lucide-react";

const articles = [
  {
    id: "1",
    title: "How to Bid on Commercial Cleaning Contracts",
    excerpt:
      "The complete 4-step estimating formula using cleanable square footage, ISSA 612 production rates, and profit margin multipliers.",
    category: "Bidding & Estimating",
    readTime: 8,
    href: "/success-hub/article/commercial-cleaning-bidding-guide",
  },
  {
    id: "2",
    title: "Commercial Cleaning Client Onboarding SOP",
    excerpt:
      "A 5-step standard operating procedure to secure building access codes, set up janitor closets, and prevent client churn in the first 60 days.",
    category: "Client & Facility Management",
    readTime: 7,
    href: "/success-hub/article/commercial-cleaning-client-onboarding",
  },
  {
    id: "3",
    title: "ISSA 612 Cleaning Production Rates Benchmark",
    excerpt:
      "Verified cleaning times and square-footage benchmarks for commercial offices, medical clinics, schools, and warehouse facilities.",
    category: "Bidding & Estimating",
    readTime: 9,
    href: "/success-hub/article/issa-production-rates-guide",
  },
  {
    id: "4",
    title: "Commercial Cleaning Crew Scheduling & Dispatch SOP",
    excerpt:
      "Standard operating procedure for scheduling commercial cleaning crews, optimizing multi-building route density, and eliminating unbillable windshield time.",
    category: "Operations & Scheduling",
    readTime: 7,
    href: "/success-hub/article/commercial-cleaning-crew-scheduling-sop",
  },
  {
    id: "5",
    title: "1099 Subcontractor vs. W-2 Employee Guide",
    excerpt:
      "A legal and operational guide to hiring 1099 cleaning subcontractors vs. W-2 employees, tracking COI expiration dates, and avoiding IRS audits.",
    category: "Staff & Compliance",
    readTime: 8,
    href: "/success-hub/article/janitorial-subcontractor-vs-employee-guide",
  },
  {
    id: "6",
    title: "Commercial Cleaning Profit Margins & Benchmarks",
    excerpt:
      "Industry benchmarks for commercial cleaning profit margins, burdened labor costs, chemical supplies, overhead ratios, and target EBITDA.",
    category: "Financials & Analytics",
    readTime: 8,
    href: "/success-hub/article/commercial-cleaning-profit-margins-benchmarks",
  },
  {
    id: "7",
    title: "How to Handle Commercial Cleaning Client Complaints",
    excerpt:
      "A proven protocol to handle commercial cleaning complaints, resolve facility manager issues within 2 hours, and turn unhappy accounts into 5-star references.",
    category: "Quality Control & Retention",
    readTime: 6,
    href: "/success-hub/article/handling-commercial-cleaning-complaints",
  },
  {
    id: "8",
    title: "Commercial Facility Walkthrough Inspection Checklist",
    excerpt:
      "Step-by-step facility walkthrough checklist for commercial cleaning contractors to capture square footage, floor care conditions, and hidden costs before bidding.",
    category: "Bidding & Operations",
    readTime: 7,
    href: "/success-hub/article/facility-walkthrough-inspection-checklist",
  },
];

export default function GettingStartedPage() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      <SEO
        title="Commercial Cleaning Operations Guides & Playbooks"
        description="Comprehensive operational guides for commercial cleaning founders: ISSA bidding formulas, client onboarding SOPs, crew scheduling, and profit margin benchmarks."
        keywords={[
          "commercial cleaning bidding guide",
          "janitorial client onboarding SOP",
          "ISSA 612 production rates",
          "cleaning business profit margins",
          "janitorial scheduling SOP",
        ]}
        url="/success-hub/getting-started"
        structuredData={[
          schemas.createBreadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Success Hub", url: "/success-hub" },
            { name: "Getting Started", url: "/success-hub/getting-started" },
          ]),
          schemas.createHowToSchema({
            name: "How to Set Up ManageClean OS for Your Commercial Cleaning Business",
            description:
              "Follow this step-by-step onboarding guide to set up your cleaning company profile, generate accurate AI proposals, and manage client facilities.",
            steps: articles.map((art) => ({
              name: art.title,
              text: art.excerpt,
              url: art.href,
            })),
          }),
        ]}
      />
      <LandingNavbar />

      {/* Header */}
      <section className="pb-12 pt-28">
        <div className="container mx-auto px-4">
          {/* Breadcrumb */}
          <Link
            to="/success-hub"
            className="mb-6 inline-flex items-center text-sm text-slate-600 transition-colors hover:text-blue-600"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Success Hub
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-start gap-6"
          >
            <div className="flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl bg-blue-100">
              <Rocket className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="mb-3 text-3xl font-bold text-slate-900 md:text-4xl">
                Getting Started
              </h1>
              <p className="max-w-2xl text-lg text-slate-600">
                New to ManageClean? Start here with account setup, first steps, and platform
                navigation.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Articles List */}
      <section className="pb-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-3xl">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="space-y-4"
            >
              {articles.map((article, index) => (
                <ArticleCard
                  key={index}
                  title={article.title}
                  excerpt={article.excerpt}
                  category={article.category}
                  categoryColor="bg-blue-100 text-blue-700"
                  readTime={article.readTime}
                  href={article.href}
                />
              ))}
            </motion.div>

            {/* Progress indicator */}
            <div className="mt-12 rounded-2xl bg-blue-50 p-6">
              <h3 className="mb-2 font-semibold text-slate-900">Ready to continue?</h3>
              <p className="mb-4 text-sm text-slate-600">
                Once you've completed the getting started guides, check out our Best Practices
                section.
              </p>
              <Link
                to="/success-hub/best-practices"
                className="inline-flex items-center font-medium text-blue-600 hover:underline"
              >
                View Best Practices
                <ChevronRight className="ml-1 h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
